import { useState } from "react"

import { Flex, Select, Steps, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { TitleStepDeployTab } from "@/pages/DeploymentsPage/components/molecules/DeployTab"
import {
  HowToUseTemplate,
  type HowToUseTemplateProps
} from "@/pages/IntegrationsPage/components/templates/HowToUseTemplate"
import { CodeItem } from "../../molecules/CodeItem"
import { getMcpClientConfigs, getMcpServerUrl } from "./helpers"

import styles from "./HowToUseKodemMCP.module.scss"

const CLIENTS = ["claudeCode", "claude", "cursor", "copilot"] as const
type Client = (typeof CLIENTS)[number]

export const HowToUseKodemMCP = (
  props: Omit<HowToUseTemplateProps, "children">
) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "integrations.kodemMCP.howToUse"
  })
  const [client, setClient] = useState<Client | null>(null)
  const setups = getMcpClientConfigs(getMcpServerUrl())
  const status = "process" as const

  const selectStep = {
    className: styles.stepSelect,
    title: (
      <Flex align="center" className={styles.wrapper} gap={8}>
        <TitleStepDeployTab prefix={t("select.prefix")} />
        <Select<Client>
          rootClassName={styles.selectRoot}
          placeholder={t("select.placeholder")}
          value={client ?? undefined}
          options={CLIENTS.map((value) => ({
            value,
            label: t(`select.${value}`)
          }))}
          onSelect={(value) => setClient(value)}
        />
      </Flex>
    )
  }

  const clientSteps = (selected: Client) => [
    {
      status,
      title: t("add.title"),
      content: (
        <div className="description-step-how-to-use-template-ks">
          <Text type="secondary">{t("add.description")}</Text>
          <CodeItem
            titleLine1={t(`add.${selected}`)}
            codeSnippet={setups[selected]}
            withCopy
          />
        </div>
      )
    },
    {
      status,
      title: t("signIn.title"),
      content: <Text type="secondary">{t("signIn.description")}</Text>
    },
    {
      status,
      title: t("verify.title"),
      content: <Text type="secondary">{t("verify.description")}</Text>
    }
  ]

  const waitStep = {
    title: t("select.wait"),
    status: "wait" as const,
    className: styles.step
  }

  const items = [selectStep, ...(client ? clientSteps(client) : [waitStep])]

  return (
    <HowToUseTemplate {...props}>
      <Steps size="small" theme="dark" orientation="vertical" items={items} />
    </HowToUseTemplate>
  )
}
